import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useLingui } from "@lingui/react/macro"
import { formatDuration } from "./call-duration"
import { useRecordingPlayback } from "@/lib/voice/recording-playback"

interface RecordingPlayerProps {
  // The voice call this recording belongs to. Keys the shared
  // playback store so the conversation-row mini player and the
  // slideout "study" scrubber drive ONE audio element and stay
  // in sync (CON-197 #6/#7).
  voiceCallId: number
  // `/v2/voice_calls/:id/recording` — our endpoint 302s to a
  // presigned R2 URL. null when there's no recording yet; the
  // player renders disabled in that case.
  src: string | null
  // Server-known recording length, used as the duration before
  // the browser has loaded the audio metadata (and in jsdom /
  // QA where the media never loads).
  durationSeconds: number | null
  // `inline` is the compact player inside the chat bubble
  // (Paper 13Q1-0); `study` is the larger pinned scrubber in
  // the transcript rail / overlay (Paper 14NZ-0) with a
  // current/total time readout. Both variants are click-to-seek
  // (CON-197 #8).
  variant: "inline" | "study"
  // Reports playback position (seconds) so the study surface
  // can highlight the active transcript turn.
  onTimeUpdate?: (currentTime: number) => void
}

// Bar pitch (bar width + gap) per variant, in px. Bars are
// `w-0.5` (2px); inline keeps a ~2px gap, study a ~1px gap —
// the dense silhouettes the player shipped with (CON-138).
// The bar COUNT derives from the measured waveform width at
// this pitch, so the silhouette fills ANY container
// edge-to-edge at the same density — instead of spreading a
// fixed count of bars thin across a wide rail or collapsing
// to a stub inside the chat bubble (the CON-199 regression).
const BAR_PITCH = { inline: 4, study: 3 } as const

// First-paint / unmeasurable-environment (jsdom) bar counts —
// the pre-measurement defaults, kept so tests stay
// deterministic without stubbing layout.
const FALLBACK_BAR_COUNT = { inline: 20, study: 24 } as const

// Keep the timestamp chip's center from poking past the
// waveform's edges on near-edge hovers.
const TOOLTIP_EDGE_PX = 16

// How long a click/tap keeps the timestamp chip up. Hover
// hides on pointerleave, but touch never reliably fires it.
const TOOLTIP_CLICK_LINGER_MS = 900

// A deterministic waveform silhouette — heights in [0.2, 1].
// Decorative; the real audio drives play/seek.
function waveformBars(count: number): number[] {
  return Array.from({ length: count }, (_, i) =>
    Math.round((0.28 + 0.62 * Math.abs(Math.sin(i * 1.27 + 0.6))) * 100) / 100,
  )
}

export function RecordingPlayer({
  voiceCallId,
  src,
  durationSeconds,
  variant,
  onTimeUpdate,
}: RecordingPlayerProps) {
  const { t } = useLingui()
  const playback = useRecordingPlayback(voiceCallId, src)
  const { isActive, isPlaying, currentTime, play, pause, seek } = playback

  const isStudy = variant === "study"

  // Measure the waveform's rendered width so the bar count
  // fills it at the canonical density. Callback-ref'd useState
  // (not useRef) so the first commit sees a real element;
  // ResizeObserver keeps the count honest when the container
  // resizes (drawer opening, viewport changes) — guarded so
  // jsdom tests don't throw.
  const [waveformEl, setWaveformEl] = useState<HTMLButtonElement | null>(
    null,
  )
  const [waveformWidth, setWaveformWidth] = useState(0)

  useLayoutEffect(() => {
    if (!waveformEl) return
    const measure = () =>
      setWaveformWidth(waveformEl.getBoundingClientRect().width)
    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(waveformEl)
    return () => observer.disconnect()
  }, [waveformEl])

  const barCount =
    waveformWidth > 0
      ? Math.max(12, Math.floor(waveformWidth / BAR_PITCH[variant]))
      : FALLBACK_BAR_COUNT[variant]
  const bars = useMemo(() => waveformBars(barCount), [barCount])

  // Prefer the browser-loaded duration (from the shared store);
  // fall back to the server value so the readout is correct
  // before / without media load.
  const duration =
    playback.duration > 0
      ? playback.duration
      : durationSeconds && durationSeconds > 0
        ? durationSeconds
        : null
  const progress = duration && duration > 0 ? currentTime / duration : 0

  // The floating timestamp chip over the waveform (CON-199):
  // follows the pointer on hover and confirms the position on
  // click/tap, so seeking is precise instead of guesswork.
  const [scrub, setScrub] = useState<{
    x: number
    seconds: number
  } | null>(null)
  const scrubLingerTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  useEffect(
    () => () => {
      if (scrubLingerTimer.current) clearTimeout(scrubLingerTimer.current)
    },
    [],
  )

  // Bubble the shared store's time up so the slideout can
  // highlight the active transcript turn. Only this recording's
  // surfaces report — when another call is active, our position
  // reads 0 and we don't clobber the panel's highlight.
  useEffect(() => {
    if (isActive) onTimeUpdate?.(currentTime)
  }, [isActive, currentTime, onTimeUpdate])

  const toggle = () => {
    if (!src) return
    if (isActive && isPlaying) pause()
    else play()
  }

  const seekTo = (seconds: number) => {
    if (!duration) return
    seek(Math.max(0, Math.min(duration, seconds)))
  }

  // Translate a pointer x into the chip position + the
  // timestamp under the cursor. null when there's nothing
  // seekable yet (no src / no duration / unmeasured width).
  const scrubAt = (
    clientX: number,
    target: HTMLButtonElement,
  ): { x: number; seconds: number } | null => {
    if (!src || !duration) return null
    const rect = target.getBoundingClientRect()
    if (rect.width === 0) return null
    const fraction = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width),
    )
    const x = Math.max(
      TOOLTIP_EDGE_PX,
      Math.min(rect.width - TOOLTIP_EDGE_PX, clientX - rect.left),
    )
    return { x, seconds: fraction * duration }
  }

  const clearScrubLinger = () => {
    if (scrubLingerTimer.current) {
      clearTimeout(scrubLingerTimer.current)
      scrubLingerTimer.current = null
    }
  }

  const handleWaveformPointerMove = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    clearScrubLinger()
    setScrub(scrubAt(event.clientX, event.currentTarget))
  }

  const handleWaveformPointerLeave = () => {
    clearScrubLinger()
    setScrub(null)
  }

  const handleWaveformClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    // Keyboard activation (Enter/Space) fires a click with
    // detail 0 and clientX 0 — ignore it so it doesn't seek to
    // the start; arrow keys handle keyboard seeking instead.
    if (event.detail === 0) return
    const rect = event.currentTarget.getBoundingClientRect()
    if (rect.width === 0) return
    seekTo(((event.clientX - rect.left) / rect.width) * (duration ?? 0))

    // Confirm the chosen timestamp: show the chip at the click
    // position, then auto-hide after a beat — on touch no
    // pointerleave ever fires to clear it.
    setScrub(scrubAt(event.clientX, event.currentTarget))
    clearScrubLinger()
    scrubLingerTimer.current = setTimeout(() => {
      setScrub(null)
      scrubLingerTimer.current = null
    }, TOOLTIP_CLICK_LINGER_MS)
  }

  // Keyboard seeking: ←/→ nudge ±5s so the scrubber is usable
  // without a pointer.
  const handleWaveformKey = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === "ArrowRight") {
      event.preventDefault()
      seekTo(currentTime + 5)
    } else if (event.key === "ArrowLeft") {
      event.preventDefault()
      seekTo(currentTime - 5)
    }
  }

  const playBtnClass = isStudy
    ? "size-10 bg-[#1F1A14]"
    : "size-7.5 bg-[#1E1B17]"
  const triangleFill = isStudy ? "#FFFFFF" : "#F0EDE6"
  const timeColor = isStudy ? "text-[#5C5040]" : "text-[#7A746B]"
  // Played / playhead / unplayed bar tints. BOTH variants show
  // progress (CON-199) — the inline row used to stay flat
  // beige, which made its click-to-seek read as broken because
  // nothing visibly responded.
  const playedColor = isStudy ? "bg-[#1F1A14]" : "bg-[#1E1B17]"
  const unplayedColor = isStudy ? "bg-[#D6CCB8]" : "bg-[#C9C2B6]"

  return (
    // The player dims as ONE unit when there's no recording to
    // play (a no-answer call, or a connected call whose recording
    // is missing / still processing) — never a separately-greyed
    // button. With a recording, the play button is solid black and
    // functional (CON-197). `w-full` so the waveform fills the
    // chat bubble instead of collapsing to its intrinsic width
    // (CON-199).
    <div
      className={`flex w-full items-center gap-3 ${src ? "" : "opacity-60"}`}
    >
      <button
        type="button"
        onClick={toggle}
        disabled={!src}
        aria-label={
          isActive && isPlaying ? t`Pause recording` : t`Play recording`
        }
        className={`flex items-center justify-center shrink-0 rounded-full ${playBtnClass}`}
      >
        {isActive && isPlaying ? (
          <PauseGlyph fill={triangleFill} />
        ) : (
          <PlayGlyph fill={triangleFill} />
        )}
      </button>

      {/* The waveform IS the seek target — the bar count derives
          from the measured width, so the silhouette fills the
          button edge-to-edge at the canonical density and the
          rendered bars' bounding box equals the click hitbox
          (CON-197 C4 / CON-199). Clickable in both variants
          (CON-197 #8); the floating chip previews the timestamp
          under the pointer. */}
      <div className="relative grow min-w-0">
        <button
          ref={setWaveformEl}
          type="button"
          onClick={handleWaveformClick}
          onKeyDown={handleWaveformKey}
          onPointerMove={handleWaveformPointerMove}
          onPointerLeave={handleWaveformPointerLeave}
          onPointerCancel={handleWaveformPointerLeave}
          tabIndex={0}
          disabled={!src}
          aria-label={t`Seek recording (arrow keys to scrub)`}
          className={`flex w-full min-w-0 items-center justify-between cursor-pointer disabled:cursor-default ${
            isStudy ? "h-7" : "h-6"
          }`}
        >
          <WaveformBars
            bars={bars}
            progress={progress}
            playedColor={playedColor}
            unplayedColor={unplayedColor}
          />
        </button>

        {scrub ? (
          <span
            aria-hidden
            data-testid="waveform-scrub-tooltip"
            className="pointer-events-none absolute -top-6 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#1E1B17] px-1.5 py-0.5 font-medium text-[#FBF7F0] text-[11px] leading-4 tabular-nums shadow-sm"
            style={{ left: `${scrub.x}px` }}
          >
            {formatDuration(scrub.seconds)}
          </span>
        ) : null}
      </div>

      <span
        className={`shrink-0 font-medium text-xs leading-4 tabular-nums ${timeColor}`}
      >
        {isStudy && duration != null
          ? `${formatDuration(currentTime)} / ${formatDuration(duration)}`
          : formatDuration(duration ?? 0)}
      </span>
    </div>
  )
}

// The bar list is memoized so the scrub chip — whose state
// updates on every pointermove — re-renders alone. With the
// width-derived bar count a wide rail renders 100+ spans;
// without the memo every hover frame would rebuild all of
// them (Copilot review, CON-199). Re-renders only when
// playback progress or the measured bar set actually change.
const WaveformBars = memo(function WaveformBars({
  bars,
  progress,
  playedColor,
  unplayedColor,
}: {
  bars: number[]
  progress: number
  playedColor: string
  unplayedColor: string
}) {
  return (
    <>
      {bars.map((height, i) => {
        const barFraction = i / bars.length
        let state: "played" | "playhead" | "unplayed" = "unplayed"
        if (progress > 0 && barFraction < progress) {
          state = "played"
        } else if (
          progress > 0 &&
          barFraction < progress + 1 / bars.length
        ) {
          state = "playhead"
        }
        const color =
          state === "played"
            ? playedColor
            : state === "playhead"
              ? "bg-[#C9A87C]"
              : unplayedColor
        return (
          <span
            key={i}
            data-testid="waveform-bar"
            data-state={state}
            className={`w-0.5 shrink-0 rounded-[1px] ${color}`}
            style={{ height: `${Math.round(height * 100)}%` }}
          />
        )
      })}
    </>
  )
})

function PlayGlyph({ fill }: { fill: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill={fill} aria-hidden>
      <path d="M8 5v14l11-7z" />
    </svg>
  )
}

function PauseGlyph({ fill }: { fill: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill={fill} aria-hidden>
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  )
}
